import React, { useContext, useEffect, useState } from 'react'

import {
  COLUMN_NAMES as DUEOUT_COLUMN_NAMES,
  DueOut,
  DueOutFetchArgs,
  getDueOutsViews,
} from '../../assets/ts/data/dueout/dueout'
import { STATUS_CODE as DUEOUT_STATUS_CODES } from '../../assets/ts/data/dueout/dueout-status-metadata'
import {
  COLUMN_NAMES as INQUIRY_COLUMN_NAMES,
  Inquiry,
  InquiryFetchArgs,
  getInquiryViews,
} from '../../assets/ts/data/inquiry/inquiry'
import { STATUS_CODE } from '../../assets/ts/data/inquiry/inquiry-status-metadata'
import { twoDigits } from '../../assets/ts/formatting/formatting'
import { scrollToTop } from '../../assets/ts/general/general'
import ExportToCSVButton from '../../components/buttons/export-to-csv-btn'
import Filters from '../../components/filters/filters'
import KPIs from '../../components/kpi/kpis'
import PageOverlayBusy from '../../components/page-overlay/page-overlay-busy'
import CompletedDueoutsTable from '../../components/table/completed-dueouts-table'
import CompletedInquiriesTable from '../../components/table/completed-inqiries-table'
import Tabs from '../../components/tabs/tabs'
import useDueOutCount from '../../hooks/dueout/use-dueout-count'
import useDueOutDistinctValues from '../../hooks/dueout/use-dueout-distinct-values'
import useDueoutViews from '../../hooks/dueout/use-dueout-view'
import useInquiryCount from '../../hooks/inquiry/use-inquiry-count'
import useInquiryDistinctValues from '../../hooks/inquiry/use-inquiry-distinct-values'
import useInquiryViews from '../../hooks/inquiry/use-inquiry-views'
import usePageAccumulator from '../../hooks/usePageAccumulator'
import PageHeader from '../../layouts/header/page-header'
import { PreviousPageContext } from '../../store/previous-page-provider'
import { RoleContext } from '../../store/role-context-provider'
import { SelectionContext } from '../../store/selection-context-provider'
import {
  ALL_SELECT_VALUE,
  ALL_SELECT_VALUE_STRING,
  DUEOUT_INITIAL_FILTER,
  INQUIRY_INITIAL_FILTER,
} from '../../utils/constants/global-constants'

type ISACTIVE = 'DUEOUTS' | 'INQUIRIES'

const CompletedInquiriesList = () => {
  const selection = useContext(SelectionContext)
  const { initialInquiryAndDueoutFilter } = useContext(RoleContext)

  const [isActive, setIsActive] = useState<ISACTIVE>('INQUIRIES')
  const [pageNum, setPageNum] = useState(0)
  const [dueoutsKPIFilter, setDueoutsKPIFilter] = useState<DueOutFetchArgs>({})
  const [inquiriesKPIFilter, setInquiriesKPIFilter] = useState<InquiryFetchArgs>({})

  const [dueoutsExportFetchIsBusy, setDueoutsExportFetchIsBusy] = useState(false)
  const [inquriesExportFetchIsBusy, setInquiriesExportFetchIsBusy] = useState(false)

  const completedInquiryInitialFilter = {
    ...INQUIRY_INITIAL_FILTER,
    isViewApplied: true,
  }
  const completedDueoutIntialFilter = {
    ...DUEOUT_INITIAL_FILTER,
    hasClosedAndCanceled: true,
    isViewApplied: true,
  }
  const dueOutPaging: DueOutFetchArgs = {
    pageNumber: pageNum,
  }
  const inquiryPaging: InquiryFetchArgs = {
    pageNumber: pageNum,
  }
  const UserRoleCodes: Partial<DueOutFetchArgs | InquiryFetchArgs> = {
    userLoginRole: {
      CSSPASCODEFilter: initialInquiryAndDueoutFilter?.cssPASCODES,
      usersAssignedComponentCode: initialInquiryAndDueoutFilter?.assignedComponentCodeFilter,
      usersMajcomCodeFilter: initialInquiryAndDueoutFilter?.majcomCodeFilter,
      usersSectionCodeFilter: initialInquiryAndDueoutFilter?.sectionCodeFilter,
      usersServicingOrganizationFilter: initialInquiryAndDueoutFilter?.servicingOrganizationFilter,
      usersServicingOrganizationWithoutSectionFilter:
        initialInquiryAndDueoutFilter?.servicingOrganizationWithoutSectionFilter,
    },
  }
  const [dueoutFilter, setDueoutFilter] = useState<DueOutFetchArgs>({
    ...DUEOUT_INITIAL_FILTER,
    hasClosedAndCanceled: true,
    isViewApplied: true,
    statusCodesFilter: [DUEOUT_STATUS_CODES.CANCELED, DUEOUT_STATUS_CODES.CLOSED],
    ...UserRoleCodes,
  })
  const [inquiryFilter, setInquiryFilter] = useState<InquiryFetchArgs>({
    ...INQUIRY_INITIAL_FILTER,
    isViewApplied: true,
    statusCodesFilter: [STATUS_CODE.CANCELED, STATUS_CODE.CLOSED, STATUS_CODE.RESOLVED],
    ...UserRoleCodes,
  })

  const { dueouts, dueoutsAreLoading } = useDueoutViews({
    ...dueoutFilter,
    ...dueOutPaging,
  })
  const { inquiries, inquiriesAreLoading } = useInquiryViews({
    ...inquiryFilter,
    ...inquiryPaging,
  })
  const {
    dueOutCountIsLoading: kpiFilteredDueOutCountIsLoading,
    dueOutCountValue: kpiFilteredDueOutCount,
  } = useDueOutCount({
    ...dueoutFilter,
    ...dueoutsKPIFilter,
  })
  const { inquiryCount, inquiryCountIsLoading, inquiryCountValue } = useInquiryCount({
    ...inquiryFilter,
    ...inquiriesKPIFilter,
  })
  const kpiFilteredDueOutCountString = kpiFilteredDueOutCount.toLocaleString()
  const inquiryCountString =
    inquiryCount.length === 1 ? inquiryCount[0].InquiryGUID_count.toLocaleString() : '--'

  // When determining the allowed values for the dashboard filters,
  // consider only entries for the current user.  Also use the same value
  // of "isArchived" from the overall list filter
  const { dueOutDistinctValues } = useDueOutDistinctValues({
    ...UserRoleCodes,
    assignedComponentCodeFilter: dueoutFilter.assignedComponentCodeFilter,
    categoryCodeFilter: dueoutFilter.categoryCodeFilter as number,
    distinctFiltersToQuery: [
      DUEOUT_COLUMN_NAMES.FY,
      DUEOUT_COLUMN_NAMES.AssignedComponentCode,
      DUEOUT_COLUMN_NAMES.ServicingMAJCOMCode,
      DUEOUT_COLUMN_NAMES.ServicingOrganization,
      DUEOUT_COLUMN_NAMES.ServicingBy,
      DUEOUT_COLUMN_NAMES.SectionCode,
      DUEOUT_COLUMN_NAMES.CategoryCode,
      DUEOUT_COLUMN_NAMES.SubCategoryCode,
      DUEOUT_COLUMN_NAMES.AssignedComponentCode,
      DUEOUT_COLUMN_NAMES.Priority,
    ],
    isArchivedFilter: dueoutFilter.isArchivedFilter,
    isViewApplied: true,
    sectionCodeFilter: dueoutFilter.sectionCodeFilter,
    servicingByFilter: dueoutFilter.servicingByFilter,
    servicingMAJCOMCodeFilter: dueoutFilter.servicingMAJCOMCodeFilter,
    servicingOrganizationFilter: dueoutFilter.servicingOrganizationFilter,
    statusCodesFilter: [DUEOUT_STATUS_CODES.CANCELED, DUEOUT_STATUS_CODES.CLOSED],
    subCategoryCodeFilter: dueoutFilter.subCategoryCodeFilter as number,
    yearFilter: dueoutFilter.yearFilter,
  })
  // When determining the allowed values for the dashboard filters,
  // consider only entries for the current user.  Also use the same value
  // of "isArchived" from the overall list filter
  const { inquiryDistinctValues } = useInquiryDistinctValues({
    ...UserRoleCodes,
    assignedComponentCodeFilter: inquiryFilter.assignedComponentCodeFilter,
    assignedToLocationFilter: inquiryFilter.assignedToLocationFilter,
    categoryCodeFilter: inquiryFilter.categoryCodeFilter as number,
    currentlyServicingByFilter: inquiryFilter.currentlyServicingByFilter,
    distinctFiltersToQuery: [
      INQUIRY_COLUMN_NAMES.FY,
      INQUIRY_COLUMN_NAMES.AssignedComponentCode,
      INQUIRY_COLUMN_NAMES.ServicingMAJCOMCode,
      INQUIRY_COLUMN_NAMES.ServicingOrganization,
      INQUIRY_COLUMN_NAMES.CurrentlyServicingBy,
      INQUIRY_COLUMN_NAMES.SectionCode,
      INQUIRY_COLUMN_NAMES.CategoryCode,
      INQUIRY_COLUMN_NAMES.SubCategoryCode,
      INQUIRY_COLUMN_NAMES.Installation,
      INQUIRY_COLUMN_NAMES.Organization,
      INQUIRY_COLUMN_NAMES.Priority,
    ],
    isArchivedFilter: inquiryFilter.isArchivedFilter,
    isViewApplied: true,
    organizationFilter: inquiryFilter.organizationFilter,
    sectionCodeFilter: inquiryFilter.sectionCodeFilter,
    servicingMAJCOMCodeFilter: inquiryFilter.servicingMAJCOMCodeFilter,
    servicingOrganizationFilter: inquiryFilter.servicingOrganizationFilter,
    statusCodesFilter: [STATUS_CODE.CANCELED, STATUS_CODE.CLOSED, STATUS_CODE.RESOLVED],
    subCategoryCodeFilter: inquiryFilter.subCategoryCodeFilter as number,
    yearFilter: inquiryFilter.yearFilter,
  })

  const getSearch = (event: { target: { value: string } }, activeTab: ISACTIVE): void => {
    const searchWord: string = event.target.value.toLocaleLowerCase()
    const updateFilter = (filterSetter: any, filter: DueOutFetchArgs | InquiryFetchArgs) => {
      filterSetter({ ...filter, searchFilter: searchWord })
    }

    switch (activeTab) {
      case 'INQUIRIES':
        updateFilter(setInquiryFilter, inquiryFilter)
        break
      case 'DUEOUTS':
        updateFilter(setDueoutFilter, dueoutFilter)
        break
      default:
        break
    }
  }
  const resetFilterOptions = (priorArgs, order, updatedArgs) => ({
    ...priorArgs,
    assignedComponentCodeFilter:
      order && order >= 2 ? priorArgs.assignedComponentCodeFilter : ALL_SELECT_VALUE,
    categoryCodeFilter: order && order >= 9 ? priorArgs.categoryCodeFilter : ALL_SELECT_VALUE,
    currentlyServicingByFilter:
      order && order >= 4 ? priorArgs.currentlyServicingByFilter : ALL_SELECT_VALUE_STRING,
    searchFilter: '',
    sectionCodeFilter: order && order >= 8 ? priorArgs.sectionCodeFilter : ALL_SELECT_VALUE,
    servicingByFilter: order && order >= 4 ? priorArgs.servicingByFilter : ALL_SELECT_VALUE_STRING,
    servicingMAJCOMCodeFilter:
      order && order >= 3 ? priorArgs.servicingMAJCOMCodeFilter : ALL_SELECT_VALUE,
    servicingOrganizationFilter:
      order && order >= 5 ? priorArgs.servicingOrganizationFilter : ALL_SELECT_VALUE_STRING,
    subCategoryCodeFilter:
      order && order >= 10 ? priorArgs.subCategoryCodeFilter : ALL_SELECT_VALUE,
    yearFilter: order && order >= 1 ? priorArgs.yearFilter : ALL_SELECT_VALUE,
    ...updatedArgs,
  })
  const updateInquiryFilter = (updatedArgs, order) => {
    const newArgs = {
      ...updatedArgs,
      statusCodesFilter: [STATUS_CODE.RESOLVED, STATUS_CODE.CLOSED, STATUS_CODE.CANCELED],
    }
    setInquiriesKPIFilter({})
    selection.reset()
    setPageNum(0)
    setInquiryFilter((priorArgs) => resetFilterOptions(priorArgs, order, newArgs))
  }
  const updateDueOutsFilter = (updatedArgs, order) => {
    const newArgs = {
      ...updatedArgs,
      statusCodesFilter: [DUEOUT_STATUS_CODES.CANCELED, DUEOUT_STATUS_CODES.CLOSED],
    }
    setDueoutsKPIFilter({})
    selection.reset()
    setPageNum(0)
    setDueoutFilter((priorArgs) => resetFilterOptions(priorArgs, order, newArgs))
  }
  const accumulatedInquiries = usePageAccumulator(pageNum, inquiries)
  const accumulatedDueOuts = usePageAccumulator(pageNum, dueouts)
  const { currentPage, setCurrentPage, setPreviousPage } = useContext(PreviousPageContext)

  const dueoutsArebusy =
    dueoutsAreLoading || kpiFilteredDueOutCountIsLoading || dueoutsExportFetchIsBusy

  const inquriesAreBusy = inquiriesAreLoading || inquiryCountIsLoading || inquriesExportFetchIsBusy

  async function getAllSelectedRecords(isActive: ISACTIVE): Promise<DueOut[] | Inquiry[]> {
    let visibleSelectedRecords: DueOut[] | Inquiry[] = []
    if (isActive === 'DUEOUTS') {
      visibleSelectedRecords = accumulatedDueOuts.filter((d) =>
        selection.keyIsSelected(d.DueOutGUID ?? '')
      )
    } else {
      visibleSelectedRecords = accumulatedInquiries.filter((d) =>
        selection.keyIsSelected(d.InquiryGUID ?? '')
      )
    }
    const unloadedSelectedRecords = await getAllUnloadedSelectedRecords(isActive)

    const allSelectedRecords: any = [...visibleSelectedRecords, ...unloadedSelectedRecords]
    return allSelectedRecords
  }

  async function getAllUnloadedSelectedRecords(isActive: ISACTIVE): Promise<DueOut[] | Inquiry[]> {
    if (selection.unloadedRecordsAreSelected && isActive === 'INQUIRIES') {
      setInquiriesExportFetchIsBusy(true)
      // Becuase the user clicked the Select All in the past, all records
      // matching the current dashboard and KPI filters that are in pages
      // past the currently loaded/visible ones need to be fetched
      const items = await getInquiryViews({
        ...inquiryFilter,
        ...inquiriesKPIFilter,
        onePageOnly: false,
        pageNumber: (inquiryPaging.pageNumber ?? 0) + 1,
      })
      setInquiriesExportFetchIsBusy(false)
      return items
    } else if (selection.unloadedRecordsAreSelected && isActive === 'DUEOUTS') {
      setDueoutsExportFetchIsBusy(true)
      const items = await getDueOutsViews({
        ...dueoutFilter,
        ...dueoutsKPIFilter,
        onePageOnly: false,
        pageNumber: (dueOutPaging.pageNumber ?? 0) + 1,
      })
      setDueoutsExportFetchIsBusy(false)
      return items
    } else {
      return []
    }
  }

  const handleBottomScrollReached = () => {
    if (inquiryCountValue > accumulatedInquiries.length) {
      setPageNum((num) => num + 1)
    }
  }

  const handleTabSelection = (tabNumber: 0 | 1) => {
    setIsActive(tabNumber === 0 ? 'INQUIRIES' : 'DUEOUTS')
    selection.reset()
    handleClearAll(tabNumber === 1 ? 'INQUIRIES' : 'DUEOUTS')
  }

  const handleClearAll = (isActive: ISACTIVE) => {
    if (isActive === 'INQUIRIES') {
      setInquiryFilter({
        ...completedInquiryInitialFilter,
        statusCodesFilter: [STATUS_CODE.RESOLVED, STATUS_CODE.CLOSED, STATUS_CODE.CANCELED],
        ...UserRoleCodes,
      })
      setInquiriesKPIFilter({})
    } else {
      setDueoutFilter({
        ...completedDueoutIntialFilter,
        statusCodesFilter: [DUEOUT_STATUS_CODES.CANCELED, DUEOUT_STATUS_CODES.CLOSED],
        ...UserRoleCodes,
      })
      setDueoutsKPIFilter({})
    }
    setPageNum(0)
    selection.reset()
  }

  const updateKpiFilter = (
    updatedArgs: DueOutFetchArgs | InquiryFetchArgs,
    tab: 'DUEOUTS' | 'INQUIRIES'
  ) => {
    setPageNum(0)
    if (tab === 'INQUIRIES') {
      setInquiriesKPIFilter((priorArgs) => {
        return {
          ...priorArgs,
          ...updatedArgs,
        }
      })
    } else {
      setDueoutsKPIFilter((priorArgs) => {
        return {
          ...priorArgs,
          ...updatedArgs,
        }
      })
    }
  }

  const tableItems = [
    {
      content: (
        <CompletedInquiriesTable
          csvTitle='COMPLETED INQUIRIES'
          inquiries={accumulatedInquiries}
          onBottomScrollReached={handleBottomScrollReached}
        />
      ),
      count: twoDigits(inquiryCountString),
      isActive: isActive === 'INQUIRIES',
      tabTitle: 'All Inquiries',
    },
    {
      content: (
        <CompletedDueoutsTable
          count={twoDigits(kpiFilteredDueOutCountString)}
          dueOuts={accumulatedDueOuts}
          onBottomScrollReached={handleBottomScrollReached}
        />
      ),
      count: twoDigits(kpiFilteredDueOutCountString),
      isActive: isActive === 'DUEOUTS',
      tabTitle: 'Due Outs',
    },
  ]

  useEffect(() => {
    selection.mergeWithAllLoadedRecordKeys(inquiries.map((i) => i.InquiryGUID ?? ''))
  }, [inquiries, isActive])

  useEffect(() => {
    selection.mergeWithAllLoadedRecordKeys(dueouts.map((i) => i.DueOutGUID ?? ''))
  }, [dueouts, isActive])

  useEffect(() => {
    scrollToTop()
  }, [])

  useEffect(() => {
    // This effect runs whenever the location changes (when the user navigates to a different route)
    if (currentPage !== location.pathname) {
      // User has navigated to a new page, so update the previous page
      setPreviousPage(currentPage) // Store the previous page
      setCurrentPage(location.pathname) // Update the current page
    }
  }, [location, currentPage]) // Runs whenever location or currentPage changes
  const ExportToCSVButtonVisibility = (): boolean => {
    if (isActive === 'INQUIRIES') {
      return inquiryCount[0]?.InquiryGUID_count > 0 ? true : false
    } else {
      return kpiFilteredDueOutCount > 0 ? true : false
    }
  }
  return (
    <>
      <div id='scroll'></div>
      {(inquriesAreBusy || dueoutsArebusy) && <PageOverlayBusy />}
      <PageHeader
        extras={<div></div>}
        icon='icon-completedinquiries font-14 me-1 color-primary'
        name='Completed Inquiries '
      />
      <div className='container'>
        <div className='row'>
          <div className='col-sm-12 col-lg listview-table'>
            <div className='shadow card w-100 h-100'>
              <div className='card-body'>
                <Tabs
                  anchorclass='title-color font-12 poppins-semibold'
                  badge='badge font-12 border-radius3 px-2 py-1 whitetext'
                  className='title-color lg'
                  isButtons={
                    ExportToCSVButtonVisibility() ? (
                      <ExportToCSVButton
                        fetchDataFunc={() => getAllSelectedRecords(isActive)}
                        pageType={
                          isActive === 'DUEOUTS' ? 'Completed/Dueouts' : 'Completed/Inquiries'
                        }
                      />
                    ) : (
                      ''
                    )
                  }
                  items={tableItems}
                  onTabSelected={handleTabSelection}
                  tablistclassName='overflow-auto'
                />
              </div>
            </div>
          </div>
          <div className='col-sm-12 listview-filters order-first order-lg-0'>
            <div className='w-100 h-100 whitebg bordered1'>
              <div className='filter-section mb-3'>
                <Filters
                  availableFilterValues={
                    isActive === 'DUEOUTS' ? dueOutDistinctValues[0] : inquiryDistinctValues[0]
                  }
                  filter={isActive === 'DUEOUTS' ? dueoutFilter : inquiryFilter}
                  getSearch={getSearch}
                  onClearAll={() => handleClearAll(isActive)}
                  onUpdateFilter={
                    isActive === 'DUEOUTS' ? updateDueOutsFilter : updateInquiryFilter
                  }
                  pageType={isActive === 'DUEOUTS' ? 'Completed/Dueouts' : 'Completed/Inquiries'}
                  tab={isActive}
                />
              </div>
              <div className='kpis-section mb-3'>
                <KPIs
                  filter={isActive === 'DUEOUTS' ? dueoutFilter : inquiryFilter}
                  pageType='COMPLETED'
                  tab={isActive}
                  updateKpiFilter={updateKpiFilter}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
export default CompletedInquiriesList
